import { css } from 'astroturf';
import React from 'react';
import { CheatsheetCard } from "./cheatsheet-card";
import { Section, PropertyRow, CodeBlock } from "./cheatsheet-elements";

const styles = css`
  .twoColumnGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .layoutExampleGrid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.25rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.5rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .layoutHeader {
    grid-column: span 3 / span 3;
    height: 1.25rem;
    background-color: var(--sheet-cyan-500-80);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .layoutNav {
    height: 2rem;
    background-color: var(--sheet-emerald-500-80);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .layoutMain {
    grid-column: span 2 / span 2;
    height: 2rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .layoutFooter {
    grid-column: span 3 / span 3;
    height: 1.25rem;
    background-color: var(--sheet-cyan-500-60);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .templateCode {
    margin-top: 0.25rem;
  }

  .autoFlowGrid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .autoFlowWideTile {
    grid-column: span 2 / span 2;
    height: 0.625rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .autoFlowHole {
    height: 0.625rem;
    background-color: var(--sheet-slate-600-40);
    border-radius: 0.125rem;
  }

  .autoFlowPlacedTile {
    height: 0.625rem;
    background-color: var(--sheet-emerald-400);
    border-radius: 0.125rem;
  }

  .caption {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.125rem;
    text-align: center;
  }

  .fourColumnGrid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .placeItemsStretch {
    display: grid;
    place-items: stretch;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .fillTile {
    width: 100%;
    height: 100%;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.125rem;
  }

  .captionUnderlined {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.0625rem;
    text-align: center;
    text-decoration-line: underline;
    text-decoration-color: var(--sheet-slate-500-50);
    text-underline-offset: 2px;
  }

  .placeItemsStart {
    display: grid;
    place-items: start;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .tinyTile {
    width: 0.75rem;
    height: 0.75rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.125rem;
  }

  .captionPlain {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.0625rem;
    text-align: center;
  }

  .placeItemsCenter {
    display: grid;
    place-items: center;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .placeItemsEnd {
    display: grid;
    place-items: end;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .alignItemsStretch {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.125rem;
    align-items: stretch;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .stretchBar {
    width: 100%;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .alignItemsStart {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.125rem;
    align-items: flex-start;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .startBarShort {
    width: 100%;
    height: 0.625rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .startBarMedium {
    width: 100%;
    height: 0.875rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .startBarTall {
    width: 100%;
    height: 0.75rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .alignItemsCenter {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.125rem;
    align-items: center;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .alignItemsEnd {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.125rem;
    align-items: flex-end;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }
`;

export function GridCheatsheet({ withBleed = false }) {
  return (
    <CheatsheetCard title="CSS Grid" icon="grid" withBleed={withBleed}>
      <div className={styles.twoColumnGrid}>
        <div>
          <Section title="Layout Setup">
            <PropertyRow property="display" values={["grid", "inline-grid"]} />
            <PropertyRow
              property="grid-template"
              values={['"header header" auto / 150px 1fr', "auto 1fr auto / 150px 1fr"]}
              description="Rows / columns shorthand, optionally with areas"
            />
            <PropertyRow property="grid-template-areas" values={['"header header" "nav main" "footer footer"']} />
            <PropertyRow property="grid-auto-flow" values={["row", "column", "dense"]} defaultValue="row" />
            <PropertyRow property="grid-auto-columns" values={["auto", "<track-size>"]} defaultValue="auto" />
            <PropertyRow property="grid-auto-rows" values={["auto", "<track-size>"]} defaultValue="auto" />
            <PropertyRow property="gap" values={["<length>", "<row> <column>"]} defaultValue="0" />
          </Section>

          <Section title="Track Sizing Functions">
            <PropertyRow property="fr" values={["1fr 2fr"]} description="Fractional unit of remaining space" />
            <PropertyRow
              property="minmax()"
              values={["minmax(100px, 1fr)"]}
              description="Min and max size constraints. Use minmax(0, 1fr) to allow shrinking."
            />
            <PropertyRow
              property="repeat()"
              values={["repeat(3, 1fr)", "repeat(auto-fit, ...)", "repeat(auto-fill, ...)"]}
              description="auto-fit: collapses. auto-fill: keeps empty tracks"
            />
            <PropertyRow
              property="fit-content()"
              values={["fit-content(300px)"]}
              description="Clamps to content or max"
            />
          </Section>

          <Section title="Place Individual Items">
            <PropertyRow property="grid-area" values={["<name>", "row-start / column-start / row-end / column-end"]} />
            <PropertyRow property="grid-column" values={["<start> / <end>", "span <n>"]} />
            <PropertyRow property="grid-row" values={["<start> / <end>", "span <n>"]} />
          </Section>

          <Section title="Align One Item">
            <PropertyRow
              property="justify-self"
              values={["start", "center", "end", "stretch"]}
              defaultValue="stretch"
            />
            <PropertyRow property="align-self" values={["start", "center", "end", "stretch"]} defaultValue="stretch" />
          </Section>
        </div>

        <div>
          <Section title="Grid Layout">
            <div className={styles.layoutExampleGrid}>
              <div className={styles.layoutHeader}>
                header
              </div>
              <div className={styles.layoutNav}>
                nav
              </div>
              <div className={styles.layoutMain}>
                main
              </div>
              <div className={styles.layoutFooter}>
                footer
              </div>
            </div>
            <CodeBlock language="css" className={styles.templateCode}>{`grid-template: "header header" auto
               "nav    main  " 1fr
               "footer footer" auto
               / 150px 1fr;`}</CodeBlock>
          </Section>

          <Section title="grid-auto-flow">
            <div className={styles.twoColumnGrid}>
              <div>
                <div className={styles.autoFlowGrid}>
                  <div className={styles.autoFlowWideTile} />
                  <div className={styles.autoFlowHole} />
                  <div className={styles.autoFlowWideTile} />
                  <div className={styles.autoFlowPlacedTile} />
                </div>
                <div className={styles.caption}>row</div>
              </div>

              <div>
                <div className={styles.autoFlowGrid} style={{gridAutoFlow:'row dense'}}>
                  <div className={styles.autoFlowWideTile} />
                  <div className={styles.autoFlowWideTile} />
                  <div className={styles.autoFlowPlacedTile} />
                </div>
                <div className={styles.caption}>dense</div>
              </div>
            </div>
          </Section>

          <Section title="place-items">
            <div className={styles.fourColumnGrid}>
              <div>
                <div className={styles.placeItemsStretch}>
                  <div className={styles.fillTile} />
                </div>
                <div className={styles.captionUnderlined}>
                  stretch
                </div>
              </div>
              <div>
                <div className={styles.placeItemsStart}>
                  <div className={styles.tinyTile} />
                </div>
                <div className={styles.captionPlain}>start</div>
              </div>
              <div>
                <div className={styles.placeItemsCenter}>
                  <div className={styles.tinyTile} />
                </div>
                <div className={styles.captionPlain}>center</div>
              </div>
              <div>
                <div className={styles.placeItemsEnd}>
                  <div className={styles.tinyTile} />
                </div>
                <div className={styles.captionPlain}>end</div>
              </div>
            </div>
          </Section>

          <Section title="align-items">
            <div className={styles.fourColumnGrid}>
              <div>
                <div className={styles.alignItemsStretch}>
                  <div className={styles.stretchBar} />
                  <div className={styles.stretchBar} />
                  <div className={styles.stretchBar} />
                </div>
                <div className={styles.captionUnderlined}>
                  stretch
                </div>
              </div>
              <div>
                <div className={styles.alignItemsStart}>
                  <div className={styles.startBarShort} />
                  <div className={styles.startBarMedium} />
                  <div className={styles.startBarTall} />
                </div>
                <div className={styles.captionPlain}>start</div>
              </div>
              <div>
                <div className={styles.alignItemsCenter}>
                  <div className={styles.startBarShort} />
                  <div className={styles.startBarMedium} />
                  <div className={styles.startBarTall} />
                </div>
                <div className={styles.captionPlain}>center</div>
              </div>
              <div>
                <div className={styles.alignItemsEnd}>
                  <div className={styles.startBarShort} />
                  <div className={styles.startBarMedium} />
                  <div className={styles.startBarTall} />
                </div>
                <div className={styles.captionPlain}>end</div>
              </div>
            </div>
          </Section>
          <Section title="Align All Items In Areas">
            <PropertyRow property="place-items" values={["<align> [justify]"]} />
            <PropertyRow
              property="justify-items"
              values={["start", "center", "end", "stretch"]}
              defaultValue="stretch"
            />
            <PropertyRow property="align-items" values={["start", "center", "end", "stretch"]} defaultValue="stretch" />
          </Section>
          <Section title="Move Whole Grid As One Block">
            <PropertyRow
              property="justify-content"
              values={["start", "center", "space-between"]}
              description="Moves the whole grid inside the container"
            />
            <PropertyRow
              property="align-content"
              values={["start", "center", "space-between", "stretch"]}
              description="Moves grid tracks when extra block space exists"
            />
          </Section>
        </div>
      </div>
    </CheatsheetCard>
  );
}
